import React, { useMemo } from 'react';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { LockBadge } from './shared/LockBadge';
import { createJerseyMap, type Team } from '../utils/jersey-resolver';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';
import {
  getPickOptionTextClass,
  getPickResultSurfaceClass,
  hasPickResult,
} from './shared/pickResultStyles';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
} from './shared/pickLockedStyles';

interface TeamPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  teams: Team[];
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  isCorrect?: boolean | null;
  disabled?: boolean;
  /**
   * Live / view-only before grading — Figma 10203:54636.
   * Selected option: muted bg, white border, lock badge. No picking.
   */
  locked?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

const TeamPick: React.FC<TeamPickProps> = ({
  questionId,
  questionText,
  subtext,
  teams,
  selectedTeam,
  onTeamSelect,
  points,
  earnedPoints,
  isCorrect,
  disabled = false,
  locked = false,
  className = '',
  questionNumber,
}) => {
  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseyMap = useMemo(() => createJerseyMap(teams), [teams]);

  // Daily Double chrome is unused for Circus Daily — ignore metadata styling.
  const isDailyDouble = false;

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : undefined}
      data-testtype="team"
    >
      {/* Subtext (if present) */}
      {subtext && (
        <span
          data-testid="team-subtext"
          className="text-muted-foreground font-sans font-normal block text-center mt-2 text-sm"
        >
          {subtext}
        </span>
      )}

      {/* Question Text */}
      <span
        data-testid="team-question"
        className="text-white font-sans font-normal block text-center mb-4 text-sm leading-5"
      >
        {questionText}
      </span>

      {/* Team Selection */}
      <div
        className={`grid gap-3 ${teams.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="team-selection"
      >
        {teams.map((team, index) => {
          const isSelected = selectedTeam === team.id;
          const hasResult = hasPickResult(isCorrect);
          const resultSurfaceClass = getPickResultSurfaceClass(isSelected, isCorrect);

          // Single column layout (more than 4 options)
          const isSingleColumn = teams.length > 4;
          const isOrphan = isOrphanCard({ index, total: teams.length, isSingleColumn });

          const getBackgroundClass = () => {
            if (resultSurfaceClass) return '';
            if (locked) return getPickLockedBackgroundClass(isSelected);
            if (isSelected) return 'bg-white';
            return 'bg-surface-muted';
          };

          const getBorderClass = () => {
            if (resultSurfaceClass) return resultSurfaceClass;
            if (locked) return getPickLockedBorderClass(isSelected);
            if (isSelected) return 'border border-white';
            return 'border border-border';
          };

          const getHoverClass = () => {
            if (disabled || hasResult || locked) return '';
            return 'hover:border-muted-foreground';
          };

          const labelClass =
            locked && isSelected
              ? getPickLockedLabelClass()
              : getPickOptionTextClass(isSelected, hasResult);

          const showLockBadge = locked && isSelected && !hasResult;

          return (
            <div
              key={team.id}
              className={`flex flex-col items-center justify-center ${
                isOrphan ? ORPHAN_CARD_CLASSES : ''
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (!disabled && !locked) onTeamSelect(isSelected ? undefined : team.id);
                }}
                disabled={disabled || locked}
                data-testid={`option-${team.id}`}
                data-testresult={
                  isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
                }
                data-selected={isSelected ? 'true' : 'false'}
                data-locked={locked ? 'true' : undefined}
                className={`
                  group peer w-full h-full ${isSingleColumn ? 'min-h-[80px]' : 'min-h-[140px]'} md:p-6 p-3 rounded-xl transition-smooth ${isSingleColumn ? 'grid grid-cols-[auto_1fr_auto] gap-4 items-center' : 'flex flex-col items-center justify-center'} relative overflow-hidden
                  ${getBackgroundClass()}
                  ${getBorderClass()}
                  ${getHoverClass()}
                  ${disabled || locked ? 'cursor-not-allowed' : ''}
                  ${disabled && !hasResult && !locked ? 'opacity-90' : ''}
                `}
              >
                {/* Popularity Percentage - Top Left */}
                {team.popularityPct !== undefined && teams.length >= 2 && (
                  <div className="absolute top-2 left-2 z-10">
                    <OptionPercentageStat
                      percentage={team.popularityPct}
                      isSelected={isSelected && !hasResult && !locked}
                    />
                  </div>
                )}

                {isSingleColumn ? (
                  <>
                    {/* Left Column: Jersey (wide enough to not overlap percentage) */}
                    <div className="relative flex justify-center pl-8">
                      <TeamJersey size="sm" jerseyUrl={teamJerseyMap.get(team.id)} />
                      {showLockBadge && (
                        <LockBadge
                          data-testid={`lock-badge-${team.id}`}
                          className="absolute bottom-0 left-1/2 -translate-x-1/2"
                        />
                      )}
                    </div>

                    {/* Middle Column: Team Name */}
                    <div className="flex items-center min-w-0">
                      <div className={`text-lg font-sans font-semibold capitalize ${labelClass}`}>
                        {team.name}
                      </div>
                    </div>

                    {/* Right Column: Secondary Logo */}
                    {team.thirdImageUrl && (
                      <div className="flex justify-center">
                        <img
                          src={team.thirdImageUrl}
                          alt="Secondary logo"
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Secondary Image - Top Right */}
                    {team.thirdImageUrl && (
                      <div className="absolute top-2 right-2 w-10 h-10">
                        <img
                          src={team.thirdImageUrl}
                          alt="Secondary logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    <div className="relative flex flex-col items-center space-y-2 mt-2 w-full max-w-[142px]">
                      {/* Team Jersey Circle */}
                      <div className="relative w-24 h-24 flex items-center justify-center">
                        <TeamJersey size="md" jerseyUrl={teamJerseyMap.get(team.id)} />
                        {showLockBadge && (
                          <LockBadge
                            data-testid={`lock-badge-${team.id}`}
                            className="absolute left-1/2 top-[calc(50%+14px)] -translate-x-1/2 -translate-y-1/2"
                          />
                        )}
                      </div>

                      {/* Team Name */}
                      <div
                        className={`${
                          team.name.split(' ').some(l => l.length > 14) ? 'text-sm' : 'text-sm'
                        } font-sans font-semibold capitalize text-center leading-4 ${labelClass}`}
                      >
                        {team.name}
                      </div>
                    </div>
                  </>
                )}
              </button>

              {/* Live Value (shown during live scoring) */}
              {team.liveValue !== undefined && (
                <div
                  data-testid={`live-value-${team.id}`}
                  className="text-sm font-medium mt-1 text-[#3bbe1e]"
                >
                  {team.liveValue}
                  {team.liveStatType ? ` ${team.liveStatType}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default TeamPick;
